import React, { useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft, Check, Phone, Mail, Star, Award as AwardIcon, Shield, ChevronRight, MapPin } from 'lucide-react';
import { Button } from '../components/ui/button';
import { FinancingCTA } from '../components/FinancingCTA';
import { Card, CardContent } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { VideoPlayer } from '../components/VideoPlayer';
import { trackClick } from '../utils/analytics';
import { services } from '../data/servicesData';

const ServicePage = () => {
  const { serviceSlug } = useParams();
  const navigate = useNavigate();
  const service = services[serviceSlug];

  useEffect(() => {
    if (service) {
      document.title = `${service.title} | North Texas Welding and Fabrication`;
    }
  }, [service]);

  if (!service) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-zinc-900 mb-4">Service Not Found</h1>
          <Button onClick={() => navigate('/')} className="bg-red-600 hover:bg-red-700">
            Back to Home
          </Button>
        </div>
      </div>
    );
  }

  const pageTitle = `${service.title} | North Texas Welding and Fabrication`;

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <meta name="description" content={service.description} />
        <link rel="canonical" href={`https://ntxpipefence.com/services/${service.slug}`} />
        <meta name="keywords" content={service.keywords} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={service.description} />
        <meta property="og:url" content={`https://ntxpipefence.com/services/${service.slug}`} />
      </Helmet>
      {/* Header */}
      <header className="bg-black border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <Button 
            onClick={() => navigate('/')}
            variant="ghost"
            className="text-white hover:text-red-600 mb-4"
          >
            <ArrowLeft className="mr-2" size={20} />
            Back to Home
          </Button>
        </div>
      </header>

      {/* Breadcrumb */}
      <nav className="bg-zinc-100 border-b border-zinc-200" aria-label="Breadcrumb" data-testid="breadcrumb">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <ol className="flex items-center flex-wrap gap-1 text-sm text-zinc-500">
            <li><Link to="/" className="hover:text-red-600 transition-colors">Home</Link></li>
            {service.isGeoHub ? (
              <>
                <li className="flex items-center gap-1"><ChevronRight size={14} /><span className="text-zinc-800 font-medium">{service.title}</span></li>
              </>
            ) : (
              <>
                <li className="flex items-center gap-1"><ChevronRight size={14} /><span className="text-zinc-800 font-medium">{service.title}</span></li>
              </>
            )}
          </ol>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="bg-zinc-900 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
                {service.title}
              </h1>
              <p className="text-zinc-300 text-lg mb-8 leading-relaxed">
                {service.description}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  onClick={() => { trackClick('Get Free Estimate', `/services/${serviceSlug}`); navigate('/#contact'); }}
                  size="lg"
                  className="bg-red-600 hover:bg-red-700 text-white font-semibold"
                >
                  Get Free Estimate
                </Button>
                <Button 
                  as="a"
                  href="tel:8177235006"
                  size="lg"
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white hover:text-black font-semibold"
                  onClick={() => trackClick('Call Phone', `/services/${serviceSlug}`)}
                >
                  Call 817-723-5006
                </Button>
              </div>
            </div>
            <div>
              <img 
                src={service.image}
                alt={service.title}
                className="rounded-lg shadow-2xl w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Long Description Section */}
      {service.longDescription && (
        <section className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl md:text-4xl font-bold text-zinc-900 mb-6">
              About {service.title}
            </h2>
            <p className="text-zinc-700 text-lg leading-relaxed">
              {service.longDescription}
            </p>
          </div>
        </section>
      )}

      {/* Benefits Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-zinc-900 mb-12 text-center">
            Why Choose Our {service.title} Services?
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {service.benefits.map((benefit) => (
              <Card key={benefit} className="border-zinc-200 hover:border-red-600 transition-all">
                <CardContent className="p-6 flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-red-600 rounded-full flex items-center justify-center">
                      <Check className="text-white" size={20} />
                    </div>
                  </div>
                  <p className="text-zinc-700 font-medium">{benefit}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Materials Section */}
      {service.materials && (
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-zinc-900 mb-4">
                {service.materials.title}
              </h2>
              <p className="text-zinc-600 text-lg">
                {service.materials.description}
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              {service.materials.items.map((item) => (
                <Card key={item.name} className="border-zinc-200">
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold text-zinc-900 mb-3">{item.name}</h3>
                    <p className="text-zinc-600 leading-relaxed">{item.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Pricing Factors Section */}
      {service.pricingFactors && (
        <section className="py-16 bg-zinc-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl md:text-4xl font-bold text-zinc-900 mb-8 text-center">
              {service.pricingFactors.title}
            </h2>
            <Card className="border-zinc-200">
              <CardContent className="p-8">
                <ul className="space-y-4 mb-6">
                  {service.pricingFactors.factors.map((factor) => (
                    <li key={factor} className="flex items-start space-x-3">
                      <Check className="text-red-600 flex-shrink-0 mt-1" size={20} />
                      <span className="text-zinc-700">{factor}</span>
                    </li>
                  ))}
                </ul>
                <div className="bg-red-50 border-l-4 border-red-600 p-6 rounded">
                  <p className="text-zinc-900 font-semibold">
                    {service.pricingFactors.note}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>
      )}

      {/* Project Process Section */}
      {service.projectProcess && (
        <section className="py-16 bg-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl md:text-4xl font-bold text-zinc-900 mb-4 text-center">
              {service.projectProcess.title}
            </h2>
            <p className="text-center text-zinc-600 mb-12 text-lg">
              <strong>Timeline:</strong> {service.projectProcess.totalTimeline}
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {service.projectProcess.steps.map((item) => (
                <Card key={item.step} className="border-zinc-200 relative">
                  <CardContent className="p-6">
                    <div className="absolute -top-4 -left-4 w-12 h-12 bg-red-600 rounded-full flex items-center justify-center text-white font-bold text-xl shadow-lg">
                      {item.step}
                    </div>
                    <h3 className="text-xl font-bold text-zinc-900 mb-3 mt-4">{item.title}</h3>
                    <p className="text-zinc-600 leading-relaxed mb-4">{item.description}</p>
                    <p className="text-sm text-red-600 font-semibold">⏱ {item.timeline}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Permitting Section */}
      {service.permitting && (
        <section className="py-16 bg-zinc-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl md:text-4xl font-bold text-zinc-900 mb-8 text-center">
              {service.permitting.title}
            </h2>
            <Card className="border-zinc-200">
              <CardContent className="p-8">
                <div className="prose prose-lg max-w-none text-zinc-700 leading-relaxed whitespace-pre-line">
                  {service.permitting.content}
                </div>
              </CardContent>
            </Card>
          </div>
        </section>
      )}

      {/* Service Areas */}
      <section className="py-16 bg-zinc-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-zinc-900 mb-8 text-center">
            Serving North Texas Communities
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 max-w-6xl mx-auto">
            {['Parker County', 'Springtown', 'Weatherford', 'Granbury', 'Decatur', 'Wise County', 'Hood County'].map((area) => (
              <div key={area} className="bg-white p-4 rounded-lg text-center border-2 border-zinc-200 hover:border-red-600 transition-all">
                <p className="font-semibold text-zinc-900 text-sm">{area}</p>
              </div>
            ))}
          </div>
          <p className="text-center text-zinc-600 mt-8">
            Also serving Azle, Bridgeport, Fort Worth, Aledo, and surrounding North Texas areas
          </p>
        </div>
      </section>

      {/* Project Gallery (if available) */}
      {service.gallery && service.gallery.length > 0 && (
        <section className="py-16 bg-zinc-50" data-testid="project-gallery">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl md:text-4xl font-bold text-zinc-900 mb-4 text-center">
              Our {service.title} Projects
            </h2>
            <p className="text-zinc-600 text-lg text-center mb-12 max-w-2xl mx-auto">
              Real projects completed by our team across North Texas
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {service.gallery.map((item) => (
                <div key={item.alt} className="relative group overflow-hidden rounded-xl shadow-lg hover:shadow-2xl transition-all">
                  {item.type === 'video' ? (
                    <VideoPlayer
                      src={item.url}
                      title={item.title}
                      aspectClass="h-64"
                    />
                  ) : (
                    <>
                      <img
                        src={item.url}
                        alt={item.alt}
                        className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                        <div className="absolute bottom-0 left-0 right-0 p-4">
                          <h4 className="text-white font-bold text-base">{item.title}</h4>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQ Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-zinc-900 mb-12 text-center">
            Frequently Asked Questions
          </h2>
          <Accordion type="single" collapsible className="w-full">
            {service.faqs.map((faq) => (
              <AccordionItem key={faq.question} value={faq.question}>
                <AccordionTrigger className="text-left text-lg font-semibold">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-zinc-600 leading-relaxed">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Geo Hub: Service Links for this Area */}
      {service.geoHubLinks && service.geoHubLinks.length > 0 && (
        <section className="py-16 bg-zinc-50" data-testid="geo-hub-links">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-zinc-900 mb-3 text-center">
              Our Services in {service.title}
            </h2>
            <p className="text-zinc-600 text-center mb-10 max-w-2xl mx-auto">
              Click any service below for detailed information, pricing, and to request a free estimate.
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {service.geoHubLinks.map((link) => (
                <Link
                  key={link.url}
                  to={link.url}
                  className="flex items-center gap-3 bg-white border border-zinc-200 rounded-lg p-4 hover:border-red-600 hover:shadow-md transition-all group"
                  data-testid={`geo-link-${link.url.replace(/\//g, '-')}`}
                >
                  <MapPin size={18} className="text-red-600 shrink-0" />
                  <span className="font-medium text-zinc-800 group-hover:text-red-600 transition-colors">{link.label}</span>
                  <ChevronRight size={16} className="ml-auto text-zinc-400 group-hover:text-red-600 transition-colors" />
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Cities Served (for geo hub pages) */}
      {service.isGeoHub && service.cities && (
        <section className="py-12 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-2xl font-bold text-zinc-900 mb-6">Communities We Serve</h2>
            <div className="flex flex-wrap justify-center gap-3">
              {service.cities.map((city) => (
                <span key={city} className="bg-zinc-100 border border-zinc-200 px-4 py-2 rounded-full text-zinc-700 font-medium text-sm">{city}</span>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Location-Specific SEO Content (skip for geo hub pages — they ARE the location content) */}
      {!service.isGeoHub && (
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-zinc-900 mb-12 text-center">
            {service.title} in Your Area
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <Card className="border-zinc-200">
              <CardContent className="p-6">
                <h3 className="text-xl font-bold text-zinc-900 mb-3">
                  {service.title} Weatherford TX
                </h3>
                <p className="text-zinc-600 mb-4">
                  Serving Weatherford and Parker County with professional {service.title.toLowerCase()} services. 
                  As the county seat, Weatherford properties get our prompt attention with free estimates and quality workmanship.
                </p>
                <p className="text-sm text-red-600 font-semibold">
                  ✓ Same-day quotes available
                </p>
              </CardContent>
            </Card>

            <Card className="border-zinc-200">
              <CardContent className="p-6">
                <h3 className="text-xl font-bold text-zinc-900 mb-3">
                  {service.title} Springtown TX
                </h3>
                <p className="text-zinc-600 mb-4">
                  Local {service.title.toLowerCase()} contractor serving Springtown and surrounding areas. 
                  We understand Springtown&apos;s rural properties and ranch needs with specialized solutions.
                </p>
                <p className="text-sm text-red-600 font-semibold">
                  ✓ Local contractor you can trust
                </p>
              </CardContent>
            </Card>

            <Card className="border-zinc-200">
              <CardContent className="p-6">
                <h3 className="text-xl font-bold text-zinc-900 mb-3">
                  {service.title} Granbury TX
                </h3>
                <p className="text-zinc-600 mb-4">
                  Granbury and Hood County {service.title.toLowerCase()} services with competitive pricing. 
                  Serving lakeside properties and ranches throughout Hood County.
                </p>
                <p className="text-sm text-red-600 font-semibold">
                  ✓ Hood County specialists
                </p>
              </CardContent>
            </Card>

            <Card className="border-zinc-200">
              <CardContent className="p-6">
                <h3 className="text-xl font-bold text-zinc-900 mb-3">
                  {service.title} Decatur TX
                </h3>
                <p className="text-zinc-600 mb-4">
                  Decatur and Wise County {service.title.toLowerCase()} experts. 
                  We serve Wise County ranches and properties with quality installations built for Texas conditions.
                </p>
                <p className="text-sm text-red-600 font-semibold">
                  ✓ Wise County coverage
                </p>
              </CardContent>
            </Card>

            <Card className="border-zinc-200">
              <CardContent className="p-6">
                <h3 className="text-xl font-bold text-zinc-900 mb-3">
                  {service.title} Azle TX
                </h3>
                <p className="text-zinc-600 mb-4">
                  Professional {service.title.toLowerCase()} in Azle and northwest Tarrant County. 
                  Quick response times for Azle properties with quality service guaranteed.
                </p>
                <p className="text-sm text-red-600 font-semibold">
                  ✓ Fast Azle service
                </p>
              </CardContent>
            </Card>

            <Card className="border-zinc-200">
              <CardContent className="p-6">
                <h3 className="text-xl font-bold text-zinc-900 mb-3">
                  {service.title} Fort Worth TX
                </h3>
                <p className="text-zinc-600 mb-4">
                  Serving Fort Worth and surrounding Tarrant County with expert {service.title.toLowerCase()}. 
                  From urban properties to ranch land, we handle projects of all sizes.
                </p>
                <p className="text-sm text-red-600 font-semibold">
                  ✓ Metro Fort Worth service
                </p>
              </CardContent>
            </Card>
          </div>

          <div className="mt-12 bg-red-50 border-l-4 border-red-600 p-8 rounded">
            <h3 className="text-2xl font-bold text-zinc-900 mb-4">
              Why Choose Local {service.title} Services?
            </h3>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-semibold text-zinc-900 mb-2">✓ Faster Response Times</h4>
                <p className="text-zinc-700">Local presence means we can visit your property quickly for estimates and start work sooner.</p>
              </div>
              <div>
                <h4 className="font-semibold text-zinc-900 mb-2">✓ Know Local Conditions</h4>
                <p className="text-zinc-700">We understand North Texas soil, weather, and terrain challenges specific to each county.</p>
              </div>
              <div>
                <h4 className="font-semibold text-zinc-900 mb-2">✓ Community Reputation</h4>
                <p className="text-zinc-700">Our reputation depends on satisfied local customers. We&apos;re invested in your community.</p>
              </div>
              <div>
                <h4 className="font-semibold text-zinc-900 mb-2">✓ Local Permits & Codes</h4>
                <p className="text-zinc-700">We navigate county-specific regulations and can assist with any permitting requirements.</p>
              </div>
            </div>
          </div>
        </div>
      </section>
      )}

      {/* CTA Section */}
      <section className="bg-zinc-900 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Get Started?
          </h2>
          <p className="text-zinc-300 text-lg mb-8">
            Contact North Texas Welding and Fabrication today for a free consultation 
            and estimate on your {service.title.toLowerCase()} project.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <Button 
              onClick={() => { trackClick('Request Free Quote - Bottom', `/services/${serviceSlug}`); navigate('/#contact'); }}
              size="lg"
              className="bg-red-600 hover:bg-red-700 text-white font-semibold"
            >
              Request Free Quote
            </Button>
            <Button 
              as="a"
              href="tel:8177235006"
              size="lg"
              variant="outline"
              className="border-2 border-white text-white hover:bg-white hover:text-black font-semibold"
              onClick={() => trackClick('Call Phone - Bottom', `/services/${serviceSlug}`)}
            >
              <Phone className="mr-2" size={20} />
              817-723-5006
            </Button>
          </div>
          <div className="flex items-center justify-center space-x-6 text-zinc-400 text-sm">
            <div className="flex items-center">
              <Mail size={16} className="mr-2" />
              <span>Northtexasweldingandfab@gmail.com</span>
            </div>
          </div>
        </div>
      </section>

      <FinancingCTA sourcePage={`/services/${serviceSlug}`} />

      {/* Footer */}
      <footer className="bg-black py-8 border-t border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-zinc-400 text-sm">
            © {new Date().getFullYear()} North Texas Welding and Fabrication, LLC. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default ServicePage;
